// CommandPalette.tsx — ⌘K / Ctrl-K navigation over screens and customers.
//
// Entirely client-side: it searches the route table plus the tenant list the
// shell already holds, so it adds no backend surface.
//
// It navigates only. No palette entry performs a mutation — a fuzzy-matched list
// where one row suspends a customer is a mis-selection away from an outage, and
// every destructive action in this console is deliberately behind a reason prompt.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { PT } from "../../platformTheme";
import { PBadge } from "./ui";

export interface PaletteItem {
  id: string;
  label: string;
  sub?: string;
  kind: "screen" | "customer";
  run: () => void;
}

export const CommandPalette: React.FC<{
  open: boolean;
  onClose: () => void;
  items: PaletteItem[];
}> = ({ open, onClose, items }) => {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (open) { setQ(""); setIdx(0); inputRef.current?.focus(); } }, [open]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items.slice(0, 24);
    // Subsequence match, so "cusdet" finds "Customer detail" — closer to how
    // people actually type into a palette than a strict substring test.
    const score = (t: string): number => {
      const l = t.toLowerCase();
      if (l.startsWith(s)) return 0;
      if (l.includes(s)) return 1;
      let i = 0;
      for (const ch of l) if (ch === s[i]) i++;
      return i === s.length ? 2 : 99;
    };
    return items
      .map((it) => ({ it, sc: Math.min(score(it.label), score(it.sub || "")) }))
      .filter((x) => x.sc < 99)
      .sort((a, b) => a.sc - b.sc)
      .slice(0, 24)
      .map((x) => x.it);
  }, [q, items]);

  useEffect(() => { setIdx(0); }, [q]);

  if (!open) return null;

  const choose = (it?: PaletteItem) => { if (!it) return; onClose(); it.run(); };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(16,24,40,.42)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "12vh 16px", zIndex: 1100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: PT.bg2, border: `1px solid ${PT.brd}`, borderRadius: PT.radiusLg,
          width: "100%", maxWidth: 560, overflow: "hidden",
          fontFamily: PT.fontUI, boxShadow: "0 24px 60px rgba(16,24,40,.22)",
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Go to a screen or customer…"
          onKeyDown={(e) => {
            if (e.key === "Escape") { e.preventDefault(); onClose(); }
            else if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); choose(results[idx]); }
          }}
          style={{
            width: "100%", boxSizing: "border-box", border: "none", outline: "none",
            fontFamily: PT.fontUI, fontSize: 14, padding: "14px 16px",
            borderBottom: `1px solid ${PT.brdSubtle}`, color: PT.tx, background: PT.bg2,
          }}
        />
        <div style={{ maxHeight: "48vh", overflowY: "auto" }}>
          {results.length === 0 ? (
            <div style={{ padding: "22px 16px", color: PT.txD, fontSize: 12.5, textAlign: "center" }}>
              Nothing matches “{q}”.
            </div>
          ) : results.map((it, i) => (
            <div
              key={it.id}
              onMouseEnter={() => setIdx(i)}
              onClick={() => choose(it)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 16px", cursor: "pointer",
                background: i === idx ? PT.accS : "transparent",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: PT.tx, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {it.label}
                </div>
                {it.sub && (
                  <div style={{ fontSize: 11, color: PT.txD, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {it.sub}
                  </div>
                )}
              </div>
              <PBadge tone={it.kind === "customer" ? "neutral" : "info"}>{it.kind}</PBadge>
            </div>
          ))}
        </div>
        <div style={{ padding: "8px 16px", borderTop: `1px solid ${PT.brdSubtle}`, fontSize: 11, color: PT.txD }}>
          ↑↓ to move · ↵ to open · esc to close · navigation only
        </div>
      </div>
    </div>
  );
};
